import { RefObject, useCallback, useEffect, useLayoutEffect, useState } from 'react';

/**
 * Live viewport rect for a popover anchor, shared by every portaled dropdown.
 *
 * Returns null while closed, and also while the anchor is scrolled out of view -
 * consumers already guard with `{open && rect && createPortal(...)}`, so the menu
 * hides instead of being left stranded mid-screen, and comes back when the anchor
 * scrolls back into view.
 *
 * The scroll listener is capture-phase on purpose: scroll events do not bubble,
 * and the anchor usually sits inside a scroller of its own (`.ds-dialog-body`, the
 * table wrapper) rather than the window.
 *
 * @param deps extra values that change the anchor's geometry (e.g. the typed
 *             input value in an autocomplete), re-measured synchronously.
 */
export function useAnchorRect(
  open: boolean,
  ref: RefObject<HTMLElement | null>,
  deps: unknown[] = [],
): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  const measure = useCallback(() => {
    const el = ref.current;
    if (el === null) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    const offscreen = r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth;
    setRect(offscreen ? null : r);
  }, [ref]);

  useLayoutEffect(() => {
    if (open) { measure(); } else { setRect(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, measure, ...deps]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  return rect;
}
